<template>
  <div class="small-12">
    <div
      v-if="!pendingGuidelines.length && curReviewerWithAccess"
      class="no-guidelines"
    >
      {{ $t('tenantProjects.submission.comment.addTechCommentHint') }}
    </div>
    <PostFitOutGuideline
      v-if="pendingGuidelines.length"
      :class="{'has-access': curReviewerWithAccess}"
      :guidelines="pendingGuidelines"
      :enableExpandByClickingTitle="isEditing"
      :expand="true"
    >
      <template
        #secTitle="secTitleProps"
      >
        <div
          v-show="isEditingId === secTitleProps.section.id"
          class="section-title-editing"
        >
          <input
            v-model="secTitleProps.section.displayOrder"
            :placeholder="$t('tenantProjects.submission.comment.section.number')"
            class="section-title-order"
            type="text"
          >
          <input
            v-model="secTitleProps.section.value"
            :placeholder="$t('tenantProjects.submission.comment.section.title')"
            class="section-title-text"
            type="text"
            @focusout="saveUpdate(null, secTitleProps.section.itemId)"
          >
        </div>
        <div v-if="isEditingId !== secTitleProps.section.id">
          <span>{{ parseOrderNumber(secTitleProps.section.displayOrder) }}</span>
          <span>{{ secTitleProps.section.value }}</span>
        </div>
        <template v-if="secTitleProps.section.actionTexts && secTitleProps.section.actionTexts.length">
          <div
            v-for="(rec, k) in secTitleProps.section.actionTexts"
            :key="`${secTitleProps.section.id}_${k}`"
            class="tip small-12"
          >
            <span class="tip__text">{{ rec }}</span>
          </div>
        </template>
      </template>
      <template
        v-if="curReviewerWithAccess"
        #secTitleAction="slotProps"
      >
        <DottedMenu>
          <HKLButton
            action="edit"
            @click="editSection(slotProps.section.id)"
          />
          <HKLButton
            action="add"
            @click="addSection(slotProps.section, slotProps.index)"
          />
          <HKLButton
            action="delete"
            @click="openDelModalForSection(slotProps.index)"
          />
        </DottedMenu>
      </template>
      <template
        #item="sectionItemProps"
      >
        <div
          v-show="isEditingId === sectionItemProps.sectionItem.id"
          class="small-12"
        >
          <!-- dynamic `ref` can't use `v-if`. -->
          <textarea
            :ref="getLineRefName(sectionItemProps.sectionItem.id)"
            v-model="sectionItemProps.sectionItem.value"
            v-autosize
            class="section-item__textarea"
            :placeholder="$t('tenantProjects.submission.comment.section.content')"
            type="text"
            @focusout="saveUpdate(sectionItemProps.section.itemId, sectionItemProps.sectionItem.itemId)"
          />
        </div>
        <template v-if="isEditingId !== sectionItemProps.sectionItem.id">
          <div class="hkl-guideline__child-number">
            {{ sectionItemProps.sectionItem.secondaryOrder === 0 ? parseOrderNumber(sectionItemProps.sectionItem.order + 1) : '' }}
          </div>
          <pre class="hkl-guideline__child-value">{{ sectionItemProps.sectionItem.value }}</pre>
        </template>
      </template>
      <template
        v-if="curReviewerWithAccess"
        #itemAction="slotProps"
      >
        <DottedMenu>
          <HKLButton
            action="edit"
            @click="editLine(slotProps.sectionItem.id)"
          />
          <HKLButton
            action="add"
            @click="addLine(slotProps.sectionItem, slotProps.index)"
          />
          <HKLButton
            action="delete"
            @click="openDelModalForLine(slotProps)"
          />
        </DottedMenu>
      </template>
    </PostFitOutGuideline>
    <HKLModal
      ref="deleteConfirmRef"
      :title="$t('deleteConfirmMsg')"
      okBtnAction="delete"
      @onOkClick="deleteSection"
    />
    <HKLModal
      ref="deleteLineConfirmRef"
      :title="$t('deleteConfirmMsg')"
      okBtnAction="delete"
      @onOkClick="deleteLine"
    />
  </div>
</template>

<script>
import HKLButton from '@/controls/Button/HKLButton'
import DottedMenu from '@/controls/DottedMenu'
import HKLModal from '@/controls/Modal/HKLModal'
import _ from 'lodash'
import {
  computed,
  getCurrentInstance,
  inject,
  onBeforeUnmount,
  onMounted,
  ref,
  unref,
  watchEffect,
} from 'vue-demi'

import { useRefModal } from '@/controls/Modal/modal.hook'
import EventBus from '@/utils/eventBus'
import { DEPENDENCY_KEYS } from '../consts'
import PostFitOutGuideline from './PostFitOutGuideline'
import { PF_HISTORY_ACTION_TYPE, PF_HISTORY_TYPE } from './consts'
import { curReviewerWithAccess, useUnderReviewPostFitout } from './hook'
import { buildCustomLine, buildCustomSection, buildGuidelineCommentsActionTexts, buildPostFitoutHistory, rebuildGuideLinesAsRaw, parseOrderNumber } from './util'
import { useStoreMutations } from '@/store'
import mutationTypes from '@/store/mutation-types'

export default {
  components: {
    HKLModal,
    HKLButton,
    DottedMenu,
    PostFitOutGuideline,
  },
  setup () {
    const { updatePendingGuideline } = useStoreMutations('postFitoutSubmission', { updatePendingGuideline: mutationTypes.TP_POST_FITOUT_UPDATE_PENDING_GUIDELINE })

    const underReviewItem = useUnderReviewPostFitout()
    const underReviewItemHistories = computed(() => (underReviewItem.value.histories))
    const pendingGuidelines = ref([])

    watchEffect(() => {
      const underReviewGuidelines = _.cloneDeep(_.get(underReviewItem.value, 'comments.guidelines', []))
      pendingGuidelines.value = underReviewGuidelines.map(buildGuidelineCommentsActionTexts(underReviewItemHistories.value))
    })
    const sortingPendingGuidelines = () => {
      pendingGuidelines.value = _.orderBy(unref(pendingGuidelines), ['order', 'secondaryOrder'], ['asc', 'asc'])
    }
    const updatePendingGuidelinesList = () => {
      const guidelines = _.cloneDeep(unref(pendingGuidelines))
      updatePendingGuideline(guidelines)
    }
    const addHistoryList = ref([])
    const removeHistoryList = ref([])
    const untickedSectionContent = (section, removingSecContent) => {
      const refValue = unref(pendingGuidelines)
      const targetParentIndex = refValue.findIndex(item => item.itemId === section.itemId)
      const targetSection = refValue[targetParentIndex]
      if (!_.isEmpty(targetSection)) {
        const removedIndex = targetSection.sectionContent.findIndex(item => item.itemId === removingSecContent.itemId)
        targetSection.sectionContent.splice(removedIndex, 1)
        // remove temporary History
        const notSaveHistoryIndex = addHistoryList.value.findIndex(item => item.guideId === removingSecContent.itemId)
        if (notSaveHistoryIndex > -1) addHistoryList.value.splice(notSaveHistoryIndex, 1)
        // remove stored History
        const removeItemHistoryIds = underReviewItemHistories.value
          .filter(item => item.guideId === removingSecContent.itemId && item.type === PF_HISTORY_TYPE.GUIDELINE)
          .map(item => item.id)
        !_.isEmpty(removeItemHistoryIds) && removeHistoryList.value.push(...removeItemHistoryIds)
        if (_.isEmpty(targetSection.sectionContent)) {
          const removeSectionHistoryIds = underReviewItemHistories.value
            .filter(item =>
              (item.parentGuideId === targetSection.itemId || item.guideId === removingSecContent.itemId) &&
              item.type === PF_HISTORY_TYPE.GUIDELINE)
            .map(item => item.id)
          !_.isEmpty(removeSectionHistoryIds) && removeHistoryList.value.push(...removeSectionHistoryIds)
          refValue.splice(targetParentIndex, 1)
        }
      }
      updatePendingGuidelinesList()
    }
    const tickedSecContent = (section, sectionContent) => {
      const refValue = unref(pendingGuidelines)
      const targetItem = refValue.find(item => item.itemId === section.itemId)
      const rawSecContent = _.omit(sectionContent, ['actionTexts', 'checked'])
      rawSecContent.secondaryOrder = 0
      if (_.isEmpty(targetItem)) {
        const item = _.omit(section, 'sectionContent')
        item.secondaryOrder = 0
        item.sectionContent = [rawSecContent]
        refValue.push(item)
      } else {
        targetItem.sectionContent.push(rawSecContent)
        targetItem.sectionContent = _.orderBy(targetItem.sectionContent, ['order', 'secondaryOrder'], ['asc', 'asc'])
      }
      const addHistory = buildPostFitoutHistory(
        PF_HISTORY_TYPE.GUIDELINE,
        PF_HISTORY_ACTION_TYPE.CHECKED,
        { parentGuideId: section.itemId, guideId: sectionContent.itemId },
      )
      addHistoryList.value.push(addHistory)
      sortingPendingGuidelines()
      updatePendingGuidelinesList()
    }
    const isEditingId = ref(null)
    const isAddingStatus = ref(false)
    const { saveComments } = inject(DEPENDENCY_KEYS.POSTFITOUT_ENTRY)
    const editSection = (id) => {
      isEditingId.value = id
    }

    const addSection = async (section, secIndex) => {
      const newSection = await buildCustomSection(section)
      pendingGuidelines.value.splice(secIndex + 1, 0, newSection)
      updatePendingGuidelinesList()
      pendingGuidelines.value.filter(item => item.order === section.order).forEach((item, index) => {
        item.secondaryOrder = index
      })

      const history = buildPostFitoutHistory(PF_HISTORY_TYPE.GUIDELINE, PF_HISTORY_ACTION_TYPE.ADD, { guideId: newSection.itemId })
      addHistoryList.value.push(history)
      const secContentHistory = buildPostFitoutHistory(PF_HISTORY_TYPE.GUIDELINE, PF_HISTORY_ACTION_TYPE.ADD, {
        guideId: newSection.sectionContent[0].itemId, parentGuideId: newSection.itemId,
      })
      addHistoryList.value.push(secContentHistory)

      isAddingStatus.value = true
      isEditingId.value = newSection.id
      sortingPendingGuidelines()
      updatePendingGuidelinesList()
    }
    const deleteConfirmRef = useRefModal()
    const deletingSectionIndex = ref(null)
    const deleteSection = async () => {
      if (!_.isNumber(deletingSectionIndex.value)) return
      const secIndex = deletingSectionIndex.value
      const section = pendingGuidelines.value[secIndex]
      const isMatchedSectionHistory = (item) => (
        (item.parentGuideId === section.itemId || item.guideId === section.itemId) &&
        item.type === PF_HISTORY_TYPE.GUIDELINE
      )
      const historyIds = underReviewItemHistories.value
        .filter(isMatchedSectionHistory)
        .map(item => item.id)
      removeHistoryList.value.push(...historyIds)
      const notSaveHistoryIndexes = addHistoryList.value.reduce((rs, item, index) => {
        if (isMatchedSectionHistory(item)) {
          rs.push(index)
        }
        return rs
      }, [])
      notSaveHistoryIndexes.forEach(index => addHistoryList.value.splice(index, 1))
      pendingGuidelines.value.splice(secIndex, 1)
      deletingSectionIndex.value = null
      const history = buildPostFitoutHistory(PF_HISTORY_TYPE.GUIDELINE, PF_HISTORY_ACTION_TYPE.DELETE, {
        guideId: section.itemId,
      })
      updatePendingGuidelinesList()
      addHistoryList.value.push(history)
      await saveComments()
    }
    const openDelModalForSection = (index) => {
      deleteConfirmRef.open()
      deletingSectionIndex.value = index
    }

    const getLineRefName = (id) => {
      return `teaxtare_${id}`
    }

    const { proxy } = getCurrentInstance()
    const editLine = (id) => {
      isEditingId.value = id
      proxy.$nextTick(() => {
        const refName = getLineRefName(id)
        proxy.$refs[refName].focus()
      })
    }

    const addLine = async (secContent, index) => {
      const newSecContent = await buildCustomLine(secContent)
      const targetSection = pendingGuidelines.value.find(item => item.id === secContent.parentId)
      targetSection.sectionContent.splice(index + 1, 0, newSecContent)
      const history = buildPostFitoutHistory(PF_HISTORY_TYPE.GUIDELINE, PF_HISTORY_ACTION_TYPE.ADD, {
        guideId: newSecContent.itemId, parentGuideId: targetSection.itemId,
      })
      addHistoryList.value.push(history)
      isAddingStatus.value = true
      editLine(newSecContent.id)
    }

    const deletingLinePayload = ref({})
    const deleteLineConfirmRef = useRefModal()
    const deleteLine = async () => {
      if (_.isEmpty(deletingLinePayload.value)) return
      const { section, sectionContent } = unref(deletingLinePayload.value)
      untickedSectionContent(section, sectionContent)
      await saveComments()
    }
    const openDelModalForLine = (itemSlotProps) => {
      deleteLineConfirmRef.open()
      deletingLinePayload.value = { section: itemSlotProps.section, sectionContent: itemSlotProps.sectionItem }
    }

    const resetState = () => {
      addHistoryList.value = []
      removeHistoryList.value = []
      updatePendingGuideline([])
    }
    const saveUpdate = async (parentGuideId, guideId) => {
      if (isAddingStatus.value) {
        isAddingStatus.value = false
      } else {
        const addHistory = buildPostFitoutHistory(PF_HISTORY_TYPE.GUIDELINE, PF_HISTORY_ACTION_TYPE.UPDATE, { parentGuideId, guideId })
        addHistoryList.value.push(addHistory)
      }
      isEditingId.value = null
      await saveComments()
    }
    onMounted(() => {
      EventBus.$on('savedCurrentPostFiout', () => resetState())
      EventBus.$on('triggerGuideline', (checked, section, sectionContent) => {
        if (checked) {
          tickedSecContent(section, sectionContent)
        } else {
          untickedSectionContent(section, sectionContent)
        }
      })
    })
    onBeforeUnmount(() => {
      EventBus.$off('savedCurrentPostFiout')
      EventBus.$off('triggerGuideline')
    })
    const getRelatedPendingGuidelines = () => ({
      pendingGuidelines: rebuildGuideLinesAsRaw(unref(pendingGuidelines)),
      addHistoryList: addHistoryList.value,
      removeHistoryList: removeHistoryList.value,
    })
    return {
      isEditing: computed(() => isEditingId.value === null),
      underReviewItemHistories,
      curReviewerWithAccess,
      isEditingId,
      saveUpdate,
      editSection,
      addSection,
      deleteSection,
      deleteConfirmRef: deleteConfirmRef.modal,
      openDelModalForSection,
      getLineRefName,
      editLine,
      addLine,
      deleteLine,
      deleteLineConfirmRef: deleteLineConfirmRef.modal,
      openDelModalForLine,
      pendingGuidelines,
      addHistoryList,
      removeHistoryList,
      getRelatedPendingGuidelines,
      parseOrderNumber,
    }
  },
}
</script>

<style lang="scss" scoped>
@import './style.scss';
.hkl-guideline {
  ::v-deep .title-container {
    margin-bottom: 5px;
  }
  ::v-deep .hkl-guideline__title {
    margin-right: 5px;
  }
  &.has-access {
    ::v-deep .title-container {
      width: calc(100% - 26px);
    }
  }
  ::v-deep &__child-box {
    align-items: flex-start;
  }
  ::v-deep &__title {
    padding: 10px 0;
    .tip {
      font-weight: normal;
      margin-top: 10px;
    }
  }
}

.no-guidelines {
  color: #878787;
  font-style: italic;
  margin-bottom: 15px;
}

.section-title {
  &-editing {
    display: flex;
  }
  &-order {
    width: 13%;
    margin-right: 10px;
  }
  &-text {
    margin-right: 5px;
  }
}

.section-item {
  &__textarea {
    width: 100%;
    letter-spacing: normal;
    line-height: 1.6;
  }
}
</style>
